import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  const handleCopyEmail = () => {
    const email = 'info@apexbitsolutions.com';
    navigator.clipboard.writeText(email).then(() => {
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    });
  };

  const handleEmailClick = () => {
    window.location.href = 'mailto:info@apexbitsolutions.com?subject=Inquiry%20from%20Website';
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const services = [
    {
      title: 'IT Consulting',
      icon: '💼',
      description: 'Expert technology consulting to drive your business forward with innovative solutions and strategic planning.'
    },
    {
      title: 'Hardware Solutions',
      icon: '🖥️',
      description: 'Comprehensive hardware infrastructure including servers, workstations, and networking equipment for businesses of all sizes.'
    },
    {
      title: 'Network Infrastructure',
      icon: '🌐',
      description: 'Design, implementation, and maintenance of robust network infrastructure to keep your business connected and secure.'
    },
    {
      title: 'Cybersecurity',
      icon: '🔒',
      description: 'Advanced security solutions to protect your business from cyber threats and ensure data integrity.'
    },
    {
      title: 'Web Development',
      icon: '🌐',
      description: 'Custom website design and development services to establish your online presence with modern, responsive solutions.'
    },
    {
      title: 'App Development',
      icon: '📱',
      description: 'Native and cross-platform mobile application development to bring your ideas to life and reach your customers anywhere.'
    }
  ];

  const partners = [
    { title: 'Dell Technologies', logo: '/images/partners/dell.png' },
    { title: 'Lenovo', logo: '/images/partners/lenovo.png' },
    { title: 'HP', logo: '/images/partners/hp.png' },
    { title: 'Apple', logo: '/images/partners/apple.png' },
    { title: 'Logitech', logo: '/images/partners/logitech.png' },
    { title: 'Jabra', logo: '/images/partners/jabra.png' },
    { title: 'Polytronic', logo: '/images/partners/polytronic.png' }
  ];

  const clients = [
    { title: 'Tech Corp', logo: '/images/clients/client1.png' },
    { title: 'Innovation Labs', logo: '/images/clients/client2.png' },
    { title: 'Digital Solutions', logo: '/images/clients/client3.png' },
    { title: 'Future Systems', logo: '/images/clients/client4.png' },
    { title: 'Global Networks', logo: '/images/clients/client5.png' },
    { title: 'Smart Tech', logo: '/images/clients/client6.png' },
    { title: 'Cloud Services', logo: '/images/clients/client7.png' },
    { title: 'Data Systems', logo: '/images/clients/client8.png' }
  ];

  return (
    <div className="App">
      {/* Navigation Bar */}
      <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <div className="logo">
            <div className="logo-icon"></div>
            <span className="logo-text">Apex Bit Solutions</span>
          </div>

          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            ☰
          </button>

          <ul className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <li><a onClick={() => scrollToSection('home')}>Home</a></li>
            <li><a onClick={() => scrollToSection('services')}>Services</a></li>
            <li><a onClick={() => scrollToSection('about')}>About</a></li>
            <li><a onClick={() => scrollToSection('partners')}>Partners</a></li>
            <li><a onClick={() => scrollToSection('contact')}>Contact</a></li>
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-overlay-image"></div>
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">Welcome to <span className="gradient-text">Apex Bit Solutions</span></h1>
          <p className="hero-subtitle">Your Trusted Partner in IT Services</p>
          <p className="hero-description">
            Empowering businesses with reliable hardware<br />
            and dedicated IT support services.
          </p>
          <div className="hero-buttons">
            <button className="btn btn-primary" onClick={() => scrollToSection('services')}>
              Explore Services
            </button>
            <button className="btn btn-secondary" onClick={() => scrollToSection('contact')}>
              Get in Touch
            </button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services">
        <div className="container">
          <div className="section-header">
            <h2>What We Do</h2>
            <div className="section-underline"></div>
            <p className="section-description">
              Comprehensive technology solutions tailored to meet your business needs
            </p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <div className="container">
          <div className="section-header">
            <h2>About Apex Bit Solutions</h2>
            <div className="section-underline"></div>
          </div>
          <div className="about-content">
            <div className="about-text">
              <h3>Strategic IT Solutions for Agile Growth</h3>
              <p>
                Apex Bit Solutions is a technology partner focused on helping businesses plan, build, and
                operate modern IT environments. We specialize in IT consulting, enterprise-grade hardware
                solutions, robust network infrastructure, cybersecurity, and custom web and mobile application
                development tailored to your unique business goals.
              </p>
              <p>
                From initial assessment and solution architecture through implementation and ongoing support, we
                take a consultative, customer-centric approach. Our team combines deep technical expertise with
                practical business understanding to deliver secure, scalable, and reliable solutions that improve
                performance, reduce risk, and create measurable operational efficiencies.
              </p>
              <p>
                Our mission is to empower organizations to grow with confidence by making technology a strategic
                advantage—not a burden. Whether you are modernizing legacy systems, strengthening your security
                posture, or expanding your digital presence, Apex Bit Solutions provides the clarity, responsiveness,
                and long-term partnership you need to stay ahead in a rapidly changing digital landscape.
              </p>
            </div>
            <div className="about-image">
              <img src="images/about-us.webp" alt="About Us" />
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section id="partners" className="partners">
        <div className="container">
          <div className="section-header">
            <h2>Our Partners</h2>
            <div className="section-underline"></div>
            <p className="section-description">
              We collaborate with industry-leading technology partners to deliver the best solutions
            </p>
          </div>
          <div className="partners-slider-container">
            <div className="partners-slider">
              {partners.map((partner, index) => (
                <div key={index} className="partner-item">
                  <div className="partner-logo">
                    <img src={partner.logo} alt={partner.title} />
                  </div>
                  <h4 className="partner-title">{partner.title}</h4>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Clients Section */}
      <section className="clients hidden">
        <div className="container">
          <div className="section-header">
            <h2>Our Clients</h2>
            <div className="section-underline"></div>
            <p className="section-description">
              Trusted by businesses who rely on us for their IT needs
            </p>
          </div>
          <div className="clients-slider-container">
            <div className="clients-slider">
              {clients.map((client, index) => (
                <div key={index} className="client-item">
                  <div className="client-logo">
                    <img src={client.logo} alt={client.title} />
                  </div>
                  <h4 className="client-title">{client.title}</h4>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="credentials">
        <div className="container">
          <div className="credentials-content">
            <div className="credential-item">
              <div className="credential-icon">✓</div>
              <div className="credential-text">
                <h4>SECP Verified</h4>
                <p>Securities & Exchange Commission of Pakistan</p>
              </div>
            </div>
            <div className="credential-divider"></div>
            <div className="credential-item">
              <div className="credential-icon">✓</div>
              <div className="credential-text">
                <h4>FBR Registered</h4>
                <p>Federal Board of Revenue</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <div className="section-header">
            <h2>Get in Touch</h2>
            <div className="section-underline"></div>
            <p className="section-description">
              Ready to transform your business with our IT solutions? Reach out to us!
            </p>
          </div>
          <div className="contact-actions">
            <button className="btn btn-contact btn-copy" onClick={handleCopyEmail}>
              <span className="btn-icon">📋</span>
              <div className="btn-content">
                {emailCopied ? (
                  <>
                    <span className="btn-action">✓ Copied to Clipboard!</span>
                  </>
                ) : (
                  <>
                    <span className="btn-action">Click to Copy</span>
                    <span className="btn-text">info@apexbitsolutions.com</span>
                  </>
                )}
              </div>
            </button>
            <button className="btn btn-contact btn-email" onClick={handleEmailClick}>
              <span className="btn-icon">📧</span>
              <div className="btn-content">
                <span className="btn-action">Open Email App</span>
                <span className="btn-text">info@apexbitsolutions.com</span>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section footer-about">
              <h3>Apex Bit Solutions</h3>
              <p>Your trusted IT partner, delivering strategic solutions for agile growth.</p>
            </div>
            <div className="footer-section">
              <h4>Quick Links</h4>
              <ul>
                <li><a onClick={() => scrollToSection('home')}>Home</a></li>
                <li><a onClick={() => scrollToSection('services')}>Services</a></li>
                <li><a onClick={() => scrollToSection('about')}>About</a></li>
                <li><a onClick={() => scrollToSection('partners')}>Partners</a></li>
                <li><a onClick={() => scrollToSection('contact')}>Contact</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Major Services</h4>
              <ul>
                <li>IT Consulting</li>
                <li>Hardware Solutions</li>
                <li>Network Infrastructure</li>
                <li>Web & App Development</li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Contact Info</h4>
              <ul className="footer-contact">

                <li><span className="contact-label">✉️</span> info@apexbitsolutions.com</li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 Apex Bit Solutions. All rights reserved. | SECP Registered IT Services Vendor</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
